<template>
  <div v-if="loadRendering" class="full-height">
    <!-- Stepper / Progress Bar -->
    <div v-if="windowWidth > 960" class="mt-4 d-flex justify-center">
      <div
        v-for="(detail, index) in activeSections"
        :key="detail.type"
        class="text-center"
      >
        <div class="d-flex align-center">
          <div>
            <v-progress-circular
              :rotate="-90"
              :size="45"
              :width="5"
              :value="isSectionActiveOrCompleted(detail) ? 100 : 0"
              :color="isSectionCompleted(detail) ? 'green' : 'primary'"
            >
              <v-icon
                size="20"
                class="font-weight-bold"
                :color="isSectionCompleted(detail) ? 'green' : 'primary'"
              >
                {{ isSectionCompleted(detail) ? "check" : detail.icon }}
              </v-icon>
            </v-progress-circular>
            <div
              :class="{
                'font-weight-bold': detail.value === selectedSection,
                'green--text': isSectionCompleted(detail),
                'primary--text': !isSectionCompleted(detail),
              }"
              class="body-2 mt-1"
            >
              {{ detail.heading }}
            </div>
          </div>
          <div
            v-if="index < activeSections.length - 1"
            style="width: 70px; border-top: 2px dotted #b1afaf"
          ></div>
        </div>
      </div>
    </div>

    <!-- Onboarding Sections -->
    <div class="mt-4">
      <PersonalDetails
        v-if="selectedSection === 0"
        :form-list="formList"
        @personal-details="setPersonalDetails($event)"
        @move-to-next-section="goToNext"
      />
      <SkillProfiles
        v-else-if="selectedSection === 1"
        :form-list="formList"
        @back-to-prev="goToPrev"
        @experience-saved="goToNext"
      />
      <Certification
        v-else-if="selectedSection === 2"
        @back-to-prev="goToPrev"
        @move-to-next-session="goToNext"
      />
      <Accreditations
        v-else-if="selectedSection === 3"
        :personalDetails="personalDetails"
        :candidate-settings="candidateSettings"
        @back-to-prev="goToPrev"
        @accreditations-saved="goToNext"
      />
      <Documents
        v-else-if="selectedSection === 4"
        :candidate-settings="candidateSettings"
        @back-to-prev="goToPrev"
        @documents-saved="goToNext"
      />
      <BankInformation
        v-else-if="selectedSection === 5"
        :payroll-general-settings="payrollGeneralSettings"
        @back-to-prev="goToPrev"
        @bank-saved="goToNext"
      />
      <DynamicCustomFields
        v-else-if="selectedSection === 6"
        :list-custom-fields="listCustomFields"
        :list-loading="listLoading"
        :candidate-id="candidateId"
        @back-to-prev="goToPrev"
        @additional-saved="goToNext"
        @updated-dynamic-form="
          retrieveDynamicFieldValues('updated-dynamic-form')
        "
      />
      <JoiningKit
        v-else-if="selectedSection === 7"
        :joining-kit-document-details="joiningKitDocumentDetails"
        :candidate-id="candidateId"
        @back-to-prev="goToPrev"
        @joining-kit-saved="goToNext"
      />
      <FormSubmit
        v-else
        @back-to-prev="goToPrev"
        @form-submitted="$emit('form-submitted')"
      />
    </div>
  </div>
</template>

<script>
import PersonalDetails from "./personal-details/PersonalDetails.vue";
import SkillProfiles from "./skill-profile/SkillProfiles.vue";
import Certification from "./certification/Certification.vue";
import Accreditations from "./accreditations/Accreditations.vue";
import Documents from "./documents/Documents.vue";
import BankInformation from "./bank-details/BankInformation.vue";
import JoiningKit from "./joining-kit/JoiningKit.vue";
import FormSubmit from "./FormSubmit.vue";
import DynamicCustomFields from "./additional/DynamicCustomFields.vue";
import {
  RETRIEVE_DYNAMIC_FIELD_VALUES,
  GET_FORM_FIELDS_AND_TYPE,
  GET_DOCUMENT_SUBTYPES,
  RETRIEVE_DOCUMENT_DETAILS,
  RETRIEVE_PAYROLL_GENERAL_SETTINGS,
} from "../../graphql/OnboardingQueries";

const ALL_SECTIONS = [
  {
    type: "personal",
    value: 0,
    icon: "person",
    heading: "Personal Information",
  },
  {
    type: "skills",
    value: 1,
    icon: "business_center",
    heading: "Work Experience",
  },
  {
    type: "certification",
    value: 2,
    icon: "description",
    heading: "Certification",
  },
  {
    type: "accreditation",
    value: 3,
    icon: "article",
    heading: "Accreditation",
  },
  {
    type: "documents",
    value: 4,
    icon: "upload_file",
    heading: "Documents",
  },
  {
    type: "bank",
    value: 5,
    icon: "account_balance",
    heading: "Bank Information",
  },
  {
    type: "custom-fields",
    value: 6,
    icon: "article",
    heading: "Additional Details",
  },
  {
    type: "joiningKit",
    value: 7,
    icon: "folder_special",
    heading: "Joining Kit",
  },
  {
    type: "submit",
    value: 8,
    icon: "checklist_rtl",
    heading: "Submit",
  },
];

export default {
  name: "Onboarding",

  components: {
    PersonalDetails,
    SkillProfiles,
    Certification,
    Accreditations,
    Documents,
    BankInformation,
    JoiningKit,
    DynamicCustomFields,
    FormSubmit,
  },

  data: () => ({
    selectedSection: null,
    personalDetails: {},
    payrollGeneralSettings: {},
    listCustomFields: [],
    listLoading: false,
    candidateId: null,
    formList: {},
    fieldLoading: false,
    hideDocumentTab: false,
    hideJoiningKitTab: false,
    joiningKitDocumentDetails: {},
    candidateSettingsLoading: false,
    candidateSettings: {},
    isInitialized: false,
  }),

  computed: {
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    loadRendering() {
      return (
        !this.fieldLoading &&
        !this.candidateSettingsLoading &&
        this.isInitialized
      );
    },
    candidateStatus() {
      return localStorage.getItem("candidateStatus");
    },
    isRestricted() {
      return (
        this.candidateSettings?.Allow_Only_Unverified_Edit?.toLowerCase() ===
          "yes" && this.candidateStatus?.toLowerCase() === "returned"
      );
    },
    activeSections() {
      let sections = ALL_SECTIONS.filter((section) => {
        if (this.isRestricted) {
          return ["accreditation", "documents", "submit"].includes(
            section.type
          );
        }

        // Standard flow filtering
        if (section.type === "documents" && this.hideDocumentTab) return false;
        if (section.type === "joiningKit" && this.hideJoiningKitTab)
          return false;
        if (
          section.type === "custom-fields" &&
          this.listCustomFields.length === 0
        )
          return false;

        return true;
      });

      // Map dynamic labels
      return sections.map((s) => {
        if (s.type === "accreditation" && this.formList[451]?.Field_Alias) {
          return { ...s, heading: this.formList[451].Field_Alias };
        }
        return s;
      });
    },
  },

  async mounted() {
    await this.fetchCandidateOnboardingSettings();
  },

  methods: {
    async fetchCandidateOnboardingSettings() {
      try {
        this.candidateSettingsLoading = true;
        const res = await this.$store.dispatch("getDropdownDetails", {
          payload: { key: ["candidate_onboarding_settings"] },
        });

        if (
          res?.data?.retrieveDropdownDetails?.dropdownDetails &&
          !res?.data?.retrieveDropdownDetails?.errorCode
        ) {
          const tempData = JSON.parse(
            res.data.retrieveDropdownDetails.dropdownDetails
          );
          const settings = tempData.find(
            (item) =>
              item.tableKey?.toLowerCase() === "candidate_onboarding_settings"
          );
          this.candidateSettings = settings?.data?.[0] || {};
        }
      } catch (err) {
        this.showError(err);
      } finally {
        this.candidateSettingsLoading = false;
        await this.initializeOnboardingFlow();
      }
    },

    async initializeOnboardingFlow() {
      try {
        // Sequence related API calls
        const coreTasks = [this.getFieldProps(), this.getDocumentSubtypes()];

        if (!this.isRestricted) {
          coreTasks.push(
            this.getJoiningKitDocumentDetails(),
            this.getPayrollGeneralSettings()
          );
        }

        await Promise.all(coreTasks);

        // Handle candidate ID and dynamic fields
        const storedCandidateId = localStorage.getItem("Candidate_Id");
        if (storedCandidateId) {
          this.candidateId = parseInt(storedCandidateId);
          if (!this.isRestricted) {
            await this.retrieveDynamicFieldValues();
          }
        }

        this.resumeSession();
      } catch (error) {
        console.error("Error in onboarding flow initialization:", error);
      } finally {
        this.isInitialized = true;
      }
    },

    resumeSession() {
      const savedScreen = localStorage.getItem("currentScreen");
      const savedValue = savedScreen ? parseInt(savedScreen) : null;

      // Check if saved value is in active sections
      if (
        savedValue !== null &&
        this.activeSections.some((s) => s.value === savedValue)
      ) {
        this.selectedSection = savedValue;
      } else {
        // Default to first active section
        this.selectedSection = this.activeSections[0]?.value || 0;
      }
    },

    goToNext() {
      const currentIndex = this.activeSections.findIndex(
        (s) => s.value === this.selectedSection
      );
      if (currentIndex < this.activeSections.length - 1) {
        this.selectedSection = this.activeSections[currentIndex + 1].value;
        localStorage.setItem("currentScreen", this.selectedSection);
      }
    },

    goToPrev() {
      const currentIndex = this.activeSections.findIndex(
        (s) => s.value === this.selectedSection
      );
      if (currentIndex > 0) {
        this.selectedSection = this.activeSections[currentIndex - 1].value;
        localStorage.setItem("currentScreen", this.selectedSection);
      }
    },

    isSectionActiveOrCompleted(section) {
      return (
        section.value === this.selectedSection ||
        this.selectedSection > section.value
      );
    },

    isSectionCompleted(section) {
      return this.selectedSection > section.value;
    },

    async getFieldProps() {
      try {
        this.fieldLoading = true;
        const res = await this.$apollo.query({
          query: GET_FORM_FIELDS_AND_TYPE,
          variables: { form_Id: 178 },
          client: "apolloClientB",
          fetchPolicy: "no-cache",
        });

        if (res.data.getFormFeildsByFormIdAndTab) {
          res.data.getFormFeildsByFormIdAndTab.formFields.forEach((list) => {
            this.$set(this.formList, list.Field_Id, list);
          });
        }
      } catch (err) {
        console.error("Error fetching field props:", err);
      } finally {
        this.fieldLoading = false;
      }
    },

    async getDocumentSubtypes() {
      try {
        this.listLoading = true;
        const { data } = await this.$apollo.query({
          query: GET_DOCUMENT_SUBTYPES,
          client: "apolloClientD",
          fetchPolicy: "no-cache",
        });

        if (data?.listDocumentSubTypeDetails?.data?.length > 0) {
          const documentSubTypes = data.listDocumentSubTypeDetails.data;
          const validDocuments = documentSubTypes.filter(
            (doc) => !doc.isDefault
          );
          this.hideDocumentTab = validDocuments.length === 0;
        } else {
          this.hideDocumentTab = true;
        }
      } catch (err) {
        this.hideDocumentTab = true;
      } finally {
        this.listLoading = false;
      }
    },

    async getJoiningKitDocumentDetails() {
      try {
        const res = await this.$apollo.query({
          query: RETRIEVE_DOCUMENT_DETAILS,
          client: "apolloClientB",
        });

        const documentDetails =
          res?.data?.retrieveDocumentDetails?.documentDetails;
        this.joiningKitDocumentDetails = documentDetails || {};
        this.hideJoiningKitTab =
          !documentDetails ||
          Object.keys(documentDetails).length === 0 ||
          !documentDetails?.documentTemplateId;
      } catch (err) {
        this.hideJoiningKitTab = true;
        this.joiningKitDocumentDetails = {};
      }
    },

    async getPayrollGeneralSettings() {
      try {
        const res = await this.$apollo.query({
          query: RETRIEVE_PAYROLL_GENERAL_SETTINGS,
          client: "apolloClientE",
          fetchPolicy: "no-cache",
        });

        if (
          res?.data?.listPayrollGeneralSettings?.listPayrollGeneralSettingsData
            ?.length > 0
        ) {
          this.payrollGeneralSettings =
            res.data.listPayrollGeneralSettings
              .listPayrollGeneralSettingsData[0] || {};
        }
      } catch (err) {
        this.payrollGeneralSettings = {};
      }
    },

    async retrieveDynamicFieldValues() {
      try {
        this.listLoading = true;
        const response = await this.$apollo.query({
          query: RETRIEVE_DYNAMIC_FIELD_VALUES,
          client: "apolloClientE",
          variables: { formId: 178, primaryId: this.candidateId },
          fetchPolicy: "no-cache",
        });

        if (
          response?.data?.retrieveDynamicFieldValues?.dynamicGroupingFieldValues
        ) {
          const tempData = JSON.parse(
            response.data.retrieveDynamicFieldValues.dynamicGroupingFieldValues
          );

          tempData.forEach((section) => {
            if (section.entries.length === 0) {
              section.entries.push({
                Custom_Field_Value_Id: null,
                fields: section.fields,
              });
            } else {
              section.entries.forEach((entry) => {
                entry.fields = JSON.parse(JSON.stringify(section.fields));
                entry.fields.forEach((field) => {
                  field.Value = entry.values[field.Custom_Field_Id];
                });
              });
            }
          });

          tempData.sort(
            (a, b) => (a.Section_Order_By || 0) - (b.Section_Order_By || 0)
          );
          this.listCustomFields = tempData;
        }
      } catch (err) {
        this.showError(err);
      } finally {
        this.listLoading = false;
      }
    },

    setPersonalDetails(basicInfo) {
      if (basicInfo?.Candidate_Id) {
        this.candidateId = parseInt(basicInfo?.Candidate_Id);
        localStorage.setItem("Candidate_Id", basicInfo.Candidate_Id);
        if (this.listCustomFields.length === 0) {
          this.retrieveDynamicFieldValues();
        }
      }
      this.personalDetails = basicInfo;
    },

    showError(err) {
      const message =
        err?.graphQLErrors?.[0]?.message ||
        "Something went wrong. It could be a possibility due to network connectivity error..";

      this.$store.commit("OPEN_SNACKBAR", {
        isOpen: true,
        message,
        type: "warning",
      });
    },
  },
};
</script>
